// src/pages/MyPage/MyTokenHistory.jsx

import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import right from "../../../assets/icons/Chevron/right_black.svg";
import left from "../../../assets/icons/Chevron/left_black.svg";
import coin from "../../../assets/icons/Token/Coin.svg";
import error from "../../../assets/icons/System/error.svg";
import { Button } from "../../../assets/components/ButtonGroup";
import useInitAppDataStore from "../../../stores/initAppData";

const formatMonthKey = (d) => {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    return `${y}-${String(m).padStart(2, "0")}`;
};

const formatMonthLabel = (key) => {
    const [y, m] = key.split("-");
    return `${y}. ${m}.`;
};


const formatMonthTextLabel = (key) => {
    if (!key) return "";
    const [, m] = key.split("-");
    return `${Number(m)}월`;
};


// iOS 포함 안전 파싱 (타임존 없는 문자열은 KST로 고정)
const toKstDate = (s) => {
    if (!s) return new Date(NaN);
    let t = String(s).trim();

    // "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
    if (t.includes(" ") && !t.includes("T")) t = t.replace(" ", "T");

    // 뒤에 Z / +09:00 같은 타임존이 없으면 KST를 붙임
    const hasTZ = /Z$|[+-]\d\d:\d\d$/.test(t);
    if (!hasTZ) t += "+09:00";

    return new Date(t);
};

const typeRankAsc = (ev) => (ev.event_type === "purchase" ? 0 : 1); // 같은 시각이면 구매 먼저


const MyTokenHistory = ({ events }) => {
    const navigate = useNavigate();
    const { getInitAppData } = useInitAppDataStore();
    const user_id = localStorage.getItem("user_id");
    const initAppData = getInitAppData(user_id);

    const todayMonthKey = formatMonthKey(new Date());
    const signupMonthKey = useMemo(() => {
        if (!initAppData?.register_date) return null;

        const d = new Date(initAppData.register_date);
        if (Number.isNaN(d.getTime())) return null;

        return formatMonthKey(d);
    }, [initAppData]);

    // 1) 이벤트 정리 + 잔액 누적 계산
    const processedEvents = useMemo(() => {
        if (!Array.isArray(events)) return [];

        const withDate = events.map((ev) => ({
            ...ev,
            dateObj: toKstDate(ev.event_time),
        }));

        const sortedAsc = [...withDate].sort((a, b) => {
            const ta = a.dateObj?.getTime?.() ?? NaN;
            const tb = b.dateObj?.getTime?.() ?? NaN;

            // Invalid Date는 뒤로
            if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
            if (Number.isNaN(ta)) return 1;
            if (Number.isNaN(tb)) return -1;

            if (ta !== tb) return ta - tb;

            // 같은 timestamp면 purchase 먼저
            const ra = typeRankAsc(a);
            const rb = typeRankAsc(b);
            if (ra !== rb) return ra - rb;

            // 그 다음은 purchase_id로 안정화
            return Number(a.purchase_id || 0) - Number(b.purchase_id || 0);
        });

        let balance = 0;
        return sortedAsc.map((ev) => {
            const purchased = Number(ev.purchased_tokens || 0);
            const used = Number(ev.used_tokens || 0);
            const delta = purchased - used;
            balance += delta;

            return {
                ...ev,
                delta,
                balanceAfter: balance,
            };
        });
    }, [events]);


    // 2) 실제로 존재하는 월 목록 (이 월들만 이동 가능)
    // const monthList = useMemo(() => {
    //     const set = new Set();
    //     processedEvents.forEach((ev) => {
    //         if (!ev.dateObj || isNaN(ev.dateObj)) return;
    //         set.add(formatMonthKey(ev.dateObj));
    //     });
    //     return Array.from(set).sort(); // 오래된 월 → 최신 월
    // }, [processedEvents]);

    // 2) 가입월 기준으로 월 목록 만들기 (가입월 ~ 오늘월)
    const resolvedSignupMonthKey = useMemo(() => {
        // 1순위: register_date에서 가져온 가입월
        if (signupMonthKey) return signupMonthKey;

        // 2순위: 이벤트 중 가장 오래된 월
        if (processedEvents.length > 0) {
            const first = processedEvents[0].dateObj; // 이미 오래된 순
            return formatMonthKey(first);
        }

        // 3순위: 아무 정보 없으면 오늘월
        return todayMonthKey;
    }, [signupMonthKey, processedEvents, todayMonthKey]);

    const monthList = useMemo(() => {
        const [startY, startM] = resolvedSignupMonthKey.split("-").map(Number);
        const [endY, endM] = todayMonthKey.split("-").map(Number);

        const list = [];
        let y = startY;
        let m = startM;

        // 가입월 ~ 오늘월까지 쭉 채우기
        while (y < endY || (y === endY && m <= endM)) {
            list.push(`${y}-${String(m).padStart(2, "0")}`);
            m += 1;
            if (m > 12) {
                m = 1;
                y += 1;
            }
        }

        return list; // 예: ["2024-11", "2024-12", "2025-01", ...]
    }, [resolvedSignupMonthKey, todayMonthKey]);

    // 3) 선택된 월 인덱스 (기본: 가장 최근 월)
    const [monthIndex, setMonthIndex] = useState(0);

    useEffect(() => {
        if (monthList.length > 0) {
            setMonthIndex(monthList.length - 1); // 최신 월
        }
    }, [monthList]);

    // 현재 선택된 월 key
    // const currentMonthKey = monthList.length > 0 ? monthList[monthIndex] : null;
    // 이번 달인지 여부 (monthList가 아예 없어도 '이번 달' 취급)
    const currentMonthKey =
        monthList.length > 0 ? monthList[monthIndex] : todayMonthKey;
    const isCurrentMonthView = currentMonthKey === todayMonthKey;

    // 4) 현재 월에 해당하는 이벤트들만 필터
    const currentMonthEvents = useMemo(() => {
        if (!currentMonthKey) return [];

        const [yStr, mStr] = currentMonthKey.split("-");
        const y = Number(yStr);
        const m = Number(mStr);

        return processedEvents.filter((ev) => {
            const d = ev.dateObj;
            return d.getFullYear() === y && d.getMonth() + 1 === m;
        });
    }, [processedEvents, currentMonthKey]);

    // 5) 현재 월 요약 (총 충전 / 총 사용 / 보유 토큰 = 월 마지막날 기준 전체 잔액)
    const summary = useMemo(() => {
        let totalPurchased = 0;
        let totalUsed = 0;

        currentMonthEvents.forEach((ev) => {
            totalPurchased += Number(ev.purchased_tokens || 0);
            totalUsed += Number(ev.used_tokens || 0);
        });

        // 최신 이벤트(내림차순) 1개
        const latestEv = [...currentMonthEvents].sort((a, b) => b.dateObj - a.dateObj)[0];
        const currentBalance = latestEv ? latestEv.balanceAfter : 0;

        return { totalPurchased, totalUsed, currentBalance };
    }, [currentMonthEvents]);

    // 6) UI용: 날짜별 그룹 (09.30 밑에 여러 줄)
    const groupedByDay = useMemo(() => {
        const groups = {};

        currentMonthEvents.forEach((ev) => {
            const d = ev.dateObj;
            const y = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");

            const dayKey = `${y}-${mm}-${dd}`;      // 정렬용
            const dayLabel = `${mm}.${dd}`;         // 표시용

            if (!groups[dayKey]) groups[dayKey] = { dayLabel, list: [] };
            groups[dayKey].list.push(ev);
        });

        return Object.entries(groups)
            .sort(([a], [b]) => (a > b ? -1 : 1)) // ✅ 날짜 최신순
            .map(([, v]) => ({
                dayLabel: v.dayLabel,
                events: v.list.sort((a, b) => b.dateObj - a.dateObj), // ✅ 이벤트 최신순
            }));
    }, [currentMonthEvents]);


    const handlePrevMonth = () => {
        if (monthIndex <= 0) return;
        setMonthIndex((idx) => idx - 1);
    };

    const handleNextMonth = () => {
        if (monthIndex >= monthList.length - 1) return;
        setMonthIndex((idx) => idx + 1);
    };

    const onClickBuyToken = () => {
        navigate("/myticket", { state: { tab: "token" } });
    };

    return (
        <div className="py-[24px] px-[16px]">
            {/* 월 네비게이션 */}
            <div className="flex items-center justify-center gap-[8px] pb-[20px]">
                <button
                    type="button"
                    onClick={handlePrevMonth}
                    disabled={monthIndex <= 0}
                    className="flex items-center justify-center w-8 h-8"
                >
                    <img
                        src={left}
                        alt="이전 달"
                        className={` ${monthIndex <= 0 ? "opacity-20" : "opacity-100"
                            }`}
                    />
                </button>

                <div className="text-t3 text-text-subtle w-[120px] text-center">
                    {currentMonthKey ? formatMonthLabel(currentMonthKey) : ""}
                </div>

                <button
                    type="button"
                    onClick={handleNextMonth}
                    disabled={monthIndex >= monthList.length - 1}
                    className="flex items-center justify-center w-8 h-8"
                >
                    <img
                        src={right}
                        alt="다음 달"
                        className={` ${monthIndex >= monthList.length - 1
                            ? "opacity-20"
                            : "opacity-100"
                            }`}
                    />
                </button>
            </div>

            {/* 상단 요약 카드 */}
            <div className="overflow-hidden pb-[40px]">
                {/* 보유 토큰 수 */}
                <div className="flex items-center justify-between bg-surface-light-primary p-[16px] rounded-t-[16px] ">
                    <div className="flex items-center gap-[6px] ">
                        <img src={coin} alt="coin" />
                        <span className="text-text-inverse text-t4">
                            {currentMonthKey
                                ? `${formatMonthTextLabel(
                                    currentMonthKey
                                )} 토큰 현황`
                                : "토큰 현황"}
                        </span>
                    </div>
                </div>

                {/* 총 사용 / 총 충전 */}
                <div className="bg-surface-light-white-layered border border-border-default rounded-b-[16px]">
                    <div className="flex items-center justify-between border-b border-border-default">
                        <span className="text-b2 text-text-subtle px-[16px] py-[17px] ">
                            보유 토큰 수
                        </span>
                        <span className="text-t4 text-text-primary p-[16px]">
                            {summary.currentBalance}개
                        </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-border-default">
                        <span className="text-b2 text-text-subtle px-[16px] py-[17px]">
                            총 사용 토큰
                        </span>
                        <span className="text-t4 text-text-subtler p-[16px]">
                            {summary.totalUsed
                                ? `-${summary.totalUsed}개`
                                : "0개"}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-b2 text-text-subtle px-[16px] py-[17px] ">
                            총 충전 토큰
                        </span>
                        <span className="text-t4 text-text-subtler p-[16px]">
                            {summary.totalPurchased
                                ? `+${summary.totalPurchased}개`
                                : "0개"}
                        </span>
                    </div>
                </div>
            </div>

            {/* 토큰 내역 헤더 */}
            <div className="flex items-center justify-between pb-[24px]">
                <span className="text-t2 text-text-subtle">토큰 내역</span>

                {/* 오른쪽: "사용/충전" | "보유 토큰" 사이 간격 넓게 */}
                <div className="flex items-center gap-[16px] text-c2 text-text-subtler text-center">
                    <span className="w-[60px]">사용 / 충전</span>
                    <span className="w-[60px]">보유 토큰</span>
                </div>
            </div>

            {/* 날짜별 그룹 리스트 */}
            {groupedByDay.length > 0 ? (
                <div className="space-y-[32px]">
                    {groupedByDay.map(({ dayLabel, events }) => (
                        <div key={dayLabel}>
                            <div className="text-b2 text-text-subtler pl-[4px] pb-[4px]">
                                {dayLabel}
                            </div>

                            <div className="space-y-[12px]">
                                {events.map((ev, idx) => {
                                    const isPurchase = ev.event_type === "purchase";
                                    const amount = isPurchase
                                        ? ev.purchased_tokens
                                        : ev.used_tokens;
                                    const sign = isPurchase ? "+" : "-";

                                    const title = isPurchase
                                        ? `토큰 ${amount}개 구매`
                                        : "이미지 생성";

                                    return (
                                        <div
                                            key={`${dayLabel}-${idx}-${ev.purchase_id ?? ""
                                                }-${ev.event_time}`}
                                            className="
                                            text-b1 text-text-subtle flex items-center justify-between 
                                            rounded-[16px] bg-surface-light-subtle pl-[16px] py-[12px]
                                        "
                                        >
                                            <div className="">
                                                {title}
                                            </div>

                                            {/* 오른쪽 숫자 두 칸도 동일한 간격 + 정렬 */}
                                            <div className="flex items-center gap-[16px]">
                                                {/* 사용/충전 토큰 수 */}
                                                <span className="w-[60px] text-center">
                                                    {sign}
                                                    {amount}
                                                </span>

                                                {/* 보유 토큰 수 */}
                                                <span className="w-[60px] text-center">
                                                    {ev.balanceAfter}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center pt-[24px]">
                    <img src={error} alt="" className="w-[48px] h-[48px]" />
                    <p className="pt-[20px] text-center text-t4 text-text-default">
                        토큰 내역이 없어요
                    </p>

                    {isCurrentMonthView && (
                        <div className="pt-[4px] w-full flex flex-col items-center">
                            <p className="text-center text-c2 text-text-subtler">
                                토큰을 구매하여 AI가 추천하는 이미지를 생성해 보세요
                            </p>
                            <div className="pt-[16px] w-[239px]">
                                <Button
                                    type="fill"
                                    onClick={onClickBuyToken}
                                    size="sm"
                                    className="text-b2"
                                >
                                    토큰 구매하기
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MyTokenHistory;
